import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { SkillAssessmentState } from '../models/skillAssessment';
import request from '../utils/request';
import { RootState } from '../utils/store';
import { signOut } from './user';

export const fetchSAHistory = createAsyncThunk(
  'skillAssessment/fetchSAHistory',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/player-insights/history');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchDataSA = createAsyncThunk(
  'skillAssessment/fetchDataSA',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('api/skill-assessments');
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchSAById = createAsyncThunk(
  'skillAssessment/fetchSAById',
  async (payload: { idSA: number }, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/api/skill-assessment/${payload.idSA}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchLessonSAById = createAsyncThunk(
  'skillAssessment/fetchSALessonById',
  async (payload: { id: number; idSA: number }, { rejectWithValue }) => {
    try {
      const time = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await request.get(
        `/api/player/lessons?id=${payload.id}&timeZone=${time}`
      );

      return { ...data, idSA: payload.idSA };
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const fetchPlayerInsighSAById = createAsyncThunk(
  'skillAssessment/fetchPlayerInsighSAById',
  async (payload: { sessionId: number }, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `api/player-insight?sessionId=${payload.sessionId}`
      );
      return data;
    } catch (e) {
      let error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

const initialState: SkillAssessmentState = {
  dataSkillAssessment: null,
  loadingDataSkillAssessment: 'unload',
  idSAHistorySelected: 0,
  loadingDetailSASelected: false,
  arrayDetailSASelected: [],
  dataHistoryPI: [],
  loadingDataSAHistory: 'unload',
  idPIselected: 0,
  loadingDetailPISelected: false,
  arrayDetailPISelected: [],
  stationSASelected: null,
  detailSASelected: null,
  errorFetchSADetail: 0,
  arrayDetailPIHistory: [],
  loadingDetailPIHistory: false,
};

export const skillAssessmentSlice = createSlice({
  name: 'skillAssessment',
  initialState,
  reducers: {
    changeIdSASelected: (state, action) => {
      state.idSAHistorySelected = action.payload;
      if (state.errorFetchSADetail !== action.payload) {
        state.errorFetchSADetail = 0;
      }
    },
    changeDataSA: (state, action) => {
      state.detailSASelected = action.payload;
      state.idSAHistorySelected = action.payload.id;
      state.stationSASelected = action.payload?.stations?.[0] || null;
    },
    changeErrorFetchSADetail: (state, action) => {
      state.errorFetchSADetail = action.payload;
    },
    changeStationSASelected: (state, action) => {
      state.stationSASelected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDataSA.pending, (state) => {
      state.loadingDataSkillAssessment = 'loading';
    });
    builder.addCase(fetchDataSA.fulfilled, (state, action) => {
      state.loadingDataSkillAssessment = 'loaded';
      if (action?.payload?.data) {
        state.dataSkillAssessment = action.payload.data;
      }
    });
    builder.addCase(fetchDataSA.rejected, (state) => {
      state.loadingDataSkillAssessment = 'loaded';
    });
    builder.addCase(fetchSAHistory.pending, (state) => {
      state.loadingDataSAHistory = 'loading';
    });
    builder.addCase(fetchSAHistory.fulfilled, (state, action) => {
      state.loadingDataSAHistory = 'loaded';
      if (action?.payload?.data) {
        state.dataHistoryPI = action.payload.data;
      }
    });
    builder.addCase(fetchSAHistory.rejected, (state) => {
      state.loadingDataSAHistory = 'loaded';
    });
    builder.addCase(fetchSAById.pending, (state) => {
      state.loadingDetailSASelected = true;
    });
    builder.addCase(fetchSAById.fulfilled, (state, action) => {
      if (action.payload.data) {
        const idx = state.arrayDetailSASelected.findIndex(
          (i) => i.id === action.payload?.id
        );
        if (idx >= 0) {
          state.arrayDetailSASelected[idx] = action.payload.data;
        } else {
          state.arrayDetailSASelected.push(action.payload.data);
        }
        state.detailSASelected = action.payload.data;
        state.loadingDetailSASelected = false;
      }
    });
    builder.addCase(fetchSAById.rejected, (state) => {
      state.loadingDetailSASelected = false;
    });
    builder.addCase(fetchLessonSAById.fulfilled, (state, action) => {
      if (action.payload.data && action.payload.code !== 30001) {
        const idx = state.arrayDetailSASelected.findIndex(
          (i) => i.id === action.payload?.idSA
        );
        if (state.detailSASelected) {
          state.detailSASelected = {
            ...state.detailSASelected,
            lessonById: action.payload.data,
          };
        }
        if (idx >= 0) {
          state.arrayDetailSASelected[idx] = {
            ...state.arrayDetailSASelected[idx],
            lessonById: action.payload.data,
          };
        }
      }
    });
    builder.addCase(fetchPlayerInsighSAById.pending, (state) => {
      state.loadingDetailPIHistory = true;
    });
    builder.addCase(fetchPlayerInsighSAById.fulfilled, (state, action) => {
      state.loadingDetailPIHistory = false;
      if (action?.payload.data?.sessionId) {
        const idx = state.arrayDetailPIHistory.findIndex(
          (i) => i.sessionId === action.payload?.data?.sessionId
        );

        if (idx >= 0) {
          state.arrayDetailPIHistory[idx] = action.payload.data;
        } else {
          state.arrayDetailPIHistory.push(action.payload.data);
        }
      }
    });
    builder.addCase(fetchPlayerInsighSAById.rejected, (state) => {
      state.loadingDetailPIHistory = false;
    });
    builder.addCase(signOut, () => ({ ...initialState }));
  },
});

export const {
  changeIdSASelected,
  changeDataSA,
  changeErrorFetchSADetail,
  changeStationSASelected,
} = skillAssessmentSlice.actions;

export const selectDataSkillAssessment = (state: RootState) =>
  state.skillAssessment.dataSkillAssessment;
export const selectLoadingDataSkillAssessment = (state: RootState) =>
  state.skillAssessment.loadingDataSkillAssessment;
export const selectSAHistory = (state: RootState) =>
  state.skillAssessment.dataHistoryPI;
export const selectLoadingSAHistory = (state: RootState) =>
  state.skillAssessment.loadingDataSAHistory;
export const selectIdSaSelected = (state: RootState) =>
  state.skillAssessment.idSAHistorySelected;
export const selectArraySASelected = (state: RootState) =>
  state.skillAssessment.arrayDetailSASelected;
export const selectLoadingDetailSASelected = (state: RootState) =>
  state.skillAssessment.loadingDetailSASelected;
export const selectDataDetailSASelected = (state: RootState) =>
  state.skillAssessment.detailSASelected;
export const selectErrorFetchSASelected = (state: RootState) =>
  state.skillAssessment.errorFetchSADetail;
export const selectStationSASelected = (state: RootState) =>
  state.skillAssessment.stationSASelected;
export const selectArrayDetailPIHistory = (state: RootState) =>
  state.skillAssessment.arrayDetailPIHistory;
export const selectLoadingSADetailHistory = (state: RootState) =>
  state.skillAssessment.loadingDetailPIHistory;

export default skillAssessmentSlice.reducer;
